import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import OTPInput from "react-otp-input";
import { FaShieldAlt, FaUserCheck } from "react-icons/fa";
import AstrologerOnboardingLayout from "../components/AstrologerOnboardingLayout";
import AstrologerMarketingPanel from "../components/AstrologerMarketingPanel";
import AstrologerProgress from "../components/AstrologerProgress";
import { clearAuthData, getPostData, getStoredAuthData, postData, saveAuthToken } from "../utils/api";
import { formatIndianPhoneNumber, isValidIndianMobile } from "../utils/phone";
import { toastifySuccess, toastifyError } from "../utils/toast";
import logo from "../assets/logo";

function IndiaFlag() {
  return (
    <span className="ao-flag" aria-hidden>
      <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="20" height="14" rx="1" fill="#fff" />
        <rect width="20" height="4.67" fill="#FF9933" />
        <rect y="9.33" width="20" height="4.67" fill="#138808" />
        <circle cx="10" cy="7" r="2" stroke="#000080" strokeWidth="0.6" fill="none" />
      </svg>
    </span>
  );
}

function PhoneIllustration() {
  return (
    <div className="ao-phone-illust" aria-hidden>
      <svg width="34" height="34" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="7" y="2.5" width="10" height="19" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
        <circle cx="12" cy="18.2" r="0.9" fill="currentColor" />
        <path d="M10 5.5h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    </div>
  );
}

export default function AstrologerRegister() {
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const phoneInputRef = useRef(null);
  const isVerifyingOtpRef = useRef(false);

  const [sendOtp, setSendOtp] = useState(false);
  const [mobileOtp, setMobileOtp] = useState("");
  const [phoneEntry, setPhoneEntry] = useState(true);
  const [phoneValue, setPhoneValue] = useState({ phoneNumber: "" });
  const [phoneErrors, setPhoneErrors] = useState({ phoneNumber: "" });
  const [sec, setSec] = useState("00");
  const [min, setMin] = useState("00");
  const [timerOn, setTimerOn] = useState(false);
  const [expireOtp, setExpireOtp] = useState(true);
  const [otpError, setOtpError] = useState("");
  const [phoneErrorModal, setPhoneErrorModal] = useState("");
  const [isLoadingOtp, setIsLoadingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [alreadyRegisteredOpen, setAlreadyRegisteredOpen] = useState(false);

  const [alreadyVerified, setAlreadyVerified] = useState(false);
  const [verifiedMobile, setVerifiedMobile] = useState("");

  useEffect(() => {
    const stored = getStoredAuthData();
    const mobile = stored?.MobileNo || stored?.Mobile || "";
    if (stored?.Astro && mobile) {
      setAlreadyVerified(true);
      setVerifiedMobile(mobile);
      setPhoneValue({ phoneNumber: String(mobile).replace(/\D/g, "").slice(-10) });
    }
  }, []);

  useEffect(() => {
    if (phoneEntry && !alreadyVerified && phoneInputRef.current) phoneInputRef.current.focus();
  }, [phoneEntry, alreadyVerified]);

  const startTimer = (duration) => {
    let remaining = duration;
    clearInterval(timerRef.current);
    const countdown = () => {
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        setTimerOn(false);
        setExpireOtp(false);
        setMin("00");
        setSec("00");
        return;
      }
      setMin(Math.floor(remaining / 60).toString().padStart(2, "0"));
      setSec((remaining % 60).toString().padStart(2, "0"));
      remaining -= 1;
    };
    countdown();
    timerRef.current = setInterval(countdown, 1000);
  };

  useEffect(() => {
    if (timerOn) startTimer(60);
    return () => clearInterval(timerRef.current);
  }, [timerOn]);

  const otpExpired = sec === "00" && min === "00" && !timerOn && sendOtp;
  const displayMobileOtp = otpExpired ? "" : mobileOtp;

  const handlePhoneInput = (e) => {
    if (e.target.name === "phoneNumber") {
      const ele = e.target.value.replace(/[^0-9]/g, "");
      setPhoneValue({ phoneNumber: ele });
      setPhoneErrorModal("");
      if (phoneErrors.phoneNumber) setPhoneErrors({ phoneNumber: "" });
    }
  };

  const checkPhoneValidation = () => {
    const newErrors = {};
    if (!phoneValue?.phoneNumber) {
      newErrors.phoneNumber = "Please enter your phone number";
    } else if (!isValidIndianMobile(phoneValue.phoneNumber)) {
      newErrors.phoneNumber = "Please enter a valid 10-digit mobile number.";
    }
    setPhoneErrors(newErrors);
    if (Object.keys(newErrors).length === 0) Check_Mobile_Registered();
  };

  const Check_Mobile_Registered = async () => {
    if (isLoadingOtp) return;
    setIsLoadingOtp(true);
    let shouldSendOtp = false;
    try {
      const val = { MobileNo: phoneValue?.phoneNumber };
      const res = await getPostData("Astrologer/CheckIfMobileRegistered", val);
      if (res?.[0]?.Message === "Astrologer Found") {
        setAlreadyRegisteredOpen(true);
      } else if (res?.[0]?.Message === "No data available") {
        shouldSendOtp = true;
      } else if (res?.[0]?.Message === "User Found") {
        setPhoneErrorModal("This number is registered as a user account, not an astrologer.");
        setPhoneValue({ phoneNumber: "" });
      } else if (!res) {
        setPhoneErrorModal("Unable to verify mobile number. Please try again.");
      }
    } catch (error) {
      console.log("Error in Check_Mobile_Registered:", error);
      setPhoneErrorModal("Unable to verify mobile number. Please try again.");
    } finally {
      setIsLoadingOtp(false);
    }

    if (shouldSendOtp) {
      await Get_OTP();
    }
  };

  const Get_OTP = async () => {
    if (isLoadingOtp) return;
    setIsLoadingOtp(true);
    try {
      const val = { MobileNo: phoneValue.phoneNumber };
      const res = await postData("SMS/GetData_SMS", val);
      if (res?.success === true) {
        setExpireOtp(true);
        setTimerOn(true);
        setPhoneEntry(false);
        setSendOtp(true);
        setOtpError("");
        setMobileOtp("");
        toastifySuccess("OTP sent successfully!");
      } else {
        setOtpError("Failed to send OTP. Please try again.");
        toastifyError("Failed to send OTP. Please try again.");
      }
    } catch (error) {
      console.log("Error in Get_OTP:", error);
      setOtpError("Failed to send OTP. Please try again.");
      toastifyError("Failed to send OTP. Please try again.");
    } finally {
      setIsLoadingOtp(false);
    }
  };

  const OtpVerify = async (otp) => {
    if (isVerifyingOtpRef.current || isVerifyingOtp) return;
    if (!expireOtp) {
      setOtpError("The OTP has expired. Please request a new one.");
      return;
    }

    isVerifyingOtpRef.current = true;
    setIsVerifyingOtp(true);
    try {
      const val = { Otp: otp, MobileNo: phoneValue.phoneNumber };
      const res = await getPostData("SMS/Check_Otp", val);
      if (res?.[0]?.Status === true || res?.[0]?.Status === "true") {
        await Get_astro();
      } else {
        setOtpError("The OTP you entered is incorrect. Please try again.");
      }
    } catch (error) {
      console.log(error);
      setOtpError("OTP verification failed. Please try again.");
    } finally {
      isVerifyingOtpRef.current = false;
      setIsVerifyingOtp(false);
    }
  };

  const handleOtpChange = (otp) => {
    setMobileOtp(otp);
    setOtpError("");
    if (otp?.length === 4) OtpVerify(otp);
  };

  const isVerified = (row) => String(row?.IsVerified) === "1";
  const hasUserName = (row) => String(row?.userName || row?.UserName || "").trim().length > 0;

  const Get_astro = async () => {
    const val = {
      MobileNo: phoneValue?.phoneNumber,
      grant_type: "password",
      Type: "A",
      Medium: "Web",
    };
    try {
      const Resdata = await getPostData("Astrologer/Astrologer_Login", val);
      const row = Resdata?.[0];
      if (!row) {
        toastifyError("Login failed. Please try again.");
        return;
      }

      if (
        row?.userName === "" &&
        String(row?.IsVerified) === "0" &&
        row?.FullName === "" &&
        String(row?.error) === "200"
      ) {
        saveAuthToken({ ...row, MobileNo: phoneValue.phoneNumber });
        navigate("/astrologer-register-personal-info");
      } else if (hasUserName(row) && isVerified(row)) {
        saveAuthToken(row);
        toastifySuccess("Successfully LogIn");
        window.location.href = "https://astrocall.live/astrologer-panel/dashboard";
      } else if (String(row?.IsVerified) === "0" && String(row?.error) === "200" && hasUserName(row)) {
        saveAuthToken({ ...row, MobileNo: row?.MobileNo || phoneValue.phoneNumber });
        navigate("/astrologer-register-personal-info");
      } else if (
        (row?.Astro === "0" || row?.Astro === 0) &&
        (row?.ID === "0" || row?.ID === 0) &&
        row?.error_description === "Blocked Your Id .Please Contact to Admin."
      ) {
        toastifyError("Blocked Your Id .Please Contact to Admin.");
        resetPhoneFlow();
      } else if (row?.error_description) {
        toastifyError(row.error_description);
      } else {
        toastifyError("Unable to continue. Please contact support.");
      }
    } catch (error) {
      console.log(error);
      toastifyError("Verification failed. Please try again.");
    }
  };

  const resetPhoneFlow = () => {
    setPhoneEntry(true);
    setSendOtp(false);
    setMobileOtp("");
    setPhoneValue({ phoneNumber: "" });
    setOtpError("");
    clearInterval(timerRef.current);
    setTimerOn(false);
    setMin("00");
    setSec("00");
  };

  const formCard = (
    <article className="ao-card ao-card--register">
      <div className="ao-card__inner">
        <header className="ao-card__brand">
          <img src={logo} alt="AstroCall logo" width={48} height={48} className="ao-card__logo" />
          <p className="ao-card__name">AstroCall</p>
          <p className="ao-card__tag">Astrologer Registration</p>
        </header>

        <AstrologerProgress currentStep={1} mobileVerified={alreadyVerified} />

        {alreadyVerified ? (
          <div className="ao-verified-state">
            <div className="ao-verified-badge" role="status">
              <span className="ao-verified-badge__icon" aria-hidden>
                ✓
              </span>
              <span>Mobile Verified</span>
            </div>

            <p className="ao-verified-number">{formatIndianPhoneNumber(verifiedMobile)}</p>
            <p className="ao-step-sub">
              Your mobile number has been verified successfully.
            </p>

            <div className="ao-verified-actions">
              <button
                type="button"
                className="ao-btn ao-btn--primary ao-btn--block"
                onClick={() => navigate("/astrologer-register-personal-info")}
              >
                Continue to Personal Info →
              </button>

              <button
                type="button"
                className="ao-btn-text"
                onClick={() => {
                  clearAuthData();
                  resetPhoneFlow();
                  setAlreadyVerified(false);
                }}
              >
                Change Number
              </button>
            </div>
          </div>
        ) : phoneEntry ? (
          <div className="ao-step-panel">
            <PhoneIllustration />
            <h2 className="ao-step-title">Verify Your Mobile Number</h2>
            <p className="ao-step-sub">
              Enter your mobile number to receive OTP and continue your registration.
            </p>

            <label className="ao-label" htmlFor="phoneNumber">
              Mobile Number
            </label>
            <div className="ao-phone">
              <div className="ao-phone__code">
                <IndiaFlag />
                +91
                <span className="ao-phone__caret" aria-hidden>
                  ▾
                </span>
              </div>
              <input
                ref={phoneInputRef}
                className="ao-input"
                type="tel"
                inputMode="numeric"
                id="phoneNumber"
                name="phoneNumber"
                maxLength={10}
                placeholder="Enter Mobile Number"
                value={phoneValue.phoneNumber}
                onChange={handlePhoneInput}
                aria-invalid={!!phoneErrors.phoneNumber}
                aria-describedby={phoneErrors.phoneNumber ? "phone-error" : undefined}
              />
            </div>
            {phoneErrors.phoneNumber && (
              <p className="ao-error mt-2" id="phone-error" role="alert">
                {phoneErrors.phoneNumber}
              </p>
            )}
            {phoneErrorModal && (
              <p className="ao-error mt-2" role="alert">
                {phoneErrorModal}
              </p>
            )}

            <button
              type="button"
              className="ao-btn ao-btn--primary ao-btn--block mt-4"
              disabled={isLoadingOtp}
              onClick={checkPhoneValidation}
            >
              {isLoadingOtp ? "Sending OTP..." : "Send OTP →"}
            </button>
          </div>
        ) : (
          <div className="ao-step-panel">
            <h2 className="ao-step-title">Verify OTP</h2>
            <p className="ao-step-sub">
              Enter the OTP sent to <strong>{formatIndianPhoneNumber(phoneValue.phoneNumber)}</strong>
            </p>

            <div className="ao-otp-wrap" role="group" aria-label="OTP input">
              <OTPInput
                value={displayMobileOtp}
                onChange={handleOtpChange}
                numInputs={4}
                renderInput={(props) => <input {...props} aria-label="OTP digit" />}
                inputType="tel"
                shouldAutoFocus
              />
            </div>
            {otpError && (
              <p className="ao-error text-center mt-2" role="alert">
                {otpError}
              </p>
            )}

            <button
              type="button"
              className="ao-btn ao-btn--primary ao-btn--block mt-4"
              disabled={isVerifyingOtp || mobileOtp.length !== 4 || otpExpired}
              onClick={() => OtpVerify(mobileOtp)}
            >
              {isVerifyingOtp ? "Verifying..." : "Verify OTP →"}
            </button>

            {timerOn && (
              <p className="ao-otp-timer">
                Resend in {min}:{sec}
              </p>
            )}

            <p className="ao-footer-note">
              Didn&apos;t receive OTP?{" "}
              <button
                type="button"
                className="ao-link"
                onClick={Get_OTP}
                disabled={timerOn || isLoadingOtp}
              >
                {isLoadingOtp ? "Sending..." : "Resend OTP"}
              </button>
            </p>

            <button type="button" className="ao-btn-text mt-3" onClick={resetPhoneFlow}>
              Change Mobile Number
            </button>
          </div>
        )}

        <div className="ao-security-box" role="note">
          <FaShieldAlt className="ao-security-box__icon" aria-hidden />
          <div>
            <p className="ao-security-box__title">Only verified astrologers can join AstroCall</p>
            <p className="ao-security-box__text">Your number is safe with us and won&apos;t be shared.</p>
          </div>
        </div>

        <p className="ao-footer-note">
          Already have an account?{" "}
          <a href="https://astrocall.live/astro-login" className="ao-link">
            Login
          </a>
        </p>
      </div>
    </article>
  );

  const alreadyRegisteredModal = alreadyRegisteredOpen ? (
    <div
      className="ao-modal-overlay"
      role="presentation"
      onClick={() => setAlreadyRegisteredOpen(false)}
    >
      <div
        className="ao-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="already-registered-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ao-modal__icon ao-modal__icon--info">
          <FaUserCheck size={22} aria-hidden />
        </div>
        <h3 className="ao-modal__title" id="already-registered-title">
          Already Registered
        </h3>
        <p className="ao-modal__text">
          This mobile number is already registered as an Astrologer on AstroCall.
          If your profile is complete, please log in to continue.
        </p>
        <div className="ao-modal__actions">
          <a
            href="https://astrocall.live/astro-login"
            className="ao-btn ao-btn--primary ao-btn--block"
          >
            Login
          </a>
          <button
            type="button"
            className="ao-link ao-modal__dismiss"
            onClick={() => setAlreadyRegisteredOpen(false)}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <AstrologerOnboardingLayout
      marketing={<AstrologerMarketingPanel />}
      formCard={formCard}
      modal={alreadyRegisteredModal}
    />
  );
}
